import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import Navbar from '../components/Navbar';
import VotingSection from '../components/VotingSection';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertCircle, LayoutDashboard } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatUtcDateTime, parseBackendUtcDateTime } from '../utils/dateTime';

const parseDateOnly = (value) => {
  if (!value || typeof value !== 'string') return null;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

export default function Dashboard() {
  const { user, isProfileComplete, missingProfileFields } = useContext(AuthContext);
  const [activities, setActivities] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [news, setNews] = useState([]);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [unpaidQuote, setUnpaidQuote] = useState([]);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [cardPermissionErrors, setCardPermissionErrors] = useState({
    activities: false,
    jobs: false,
    news: false
  });

  useEffect(() => {
    fetchDashboardData();
    fetchUnpaidQuote();
  }, []);

  const fetchDashboardData = async () => {
    const [activitiesRes, jobsRes, newsRes, paymentRes] = await Promise.allSettled([
      axios.get('/activities'),
      axios.get('/jobs'),
      axios.get('/member-news'),
      axios.get('/payments/status')
    ]);

    const nextPermissionErrors = {
      activities: false,
      jobs: false,
      news: false
    };

    if (activitiesRes.status === 'fulfilled') {
      const now = new Date();
      const futureActivities = activitiesRes.value.data.filter(a => {
        const start = parseBackendUtcDateTime(a.start_datetime);
        return start instanceof Date && start > now;
      });
      setActivities(futureActivities.slice(0, 3));
    } else if (activitiesRes.reason?.response?.status === 403) {
      setActivities([]);
      nextPermissionErrors.activities = true;
    } else {
      console.error('Failed to fetch activities for dashboard', activitiesRes.reason);
    }

    if (jobsRes.status === 'fulfilled') {
      setJobs(jobsRes.value.data.slice(0, 3));
    } else if (jobsRes.reason?.response?.status === 403) {
      setJobs([]);
      nextPermissionErrors.jobs = true;
    } else {
      console.error('Failed to fetch jobs for dashboard', jobsRes.reason);
    }

    if (newsRes.status === 'fulfilled') {
      setNews(newsRes.value.data.slice(0, 3));
    } else if (newsRes.reason?.response?.status === 403) {
      setNews([]);
      nextPermissionErrors.news = true;
    } else {
      console.error('Failed to fetch member news for dashboard', newsRes.reason);
    }

    if (paymentRes.status === 'fulfilled') {
      setPaymentStatus(paymentRes.value.data);
    } else {
      console.error('Failed to fetch payment status for dashboard', paymentRes.reason);
    }

    setCardPermissionErrors(nextPermissionErrors);
  };

  const fetchUnpaidQuote = async () => {
    try {
      const response = await axios.get('/quote-associative/my-unpaid');
      setUnpaidQuote(response.data);
    } catch (error) {
      console.error('Failed to fetch unpaid quote', error);
    }
  };

  const handlePayQuota = async (quotaId) => {
    try {
      const response = await axios.post(`/payments/create-checkout-session-quota?quota_id=${quotaId}`);
      // Reindirizza a Stripe Checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Failed to create checkout session', error);
      alert('Errore nella creazione della sessione di pagamento');
    }
  };

  const currentPeriodUnpaidQuote = unpaidQuote.filter((quota) => {
    const startDate = parseDateOnly(quota.data_inizio);
    const endDate = parseDateOnly(quota.data_fine);
    if (!startDate || !endDate) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);

    return today >= startDate && today <= endDate;
  });

  return (
    <div className="min-h-screen bg-white" data-testid="dashboard-page">
      <Navbar />

      <div className="pt-20 p-6 max-w-7xl mx-auto">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <LayoutDashboard className="h-6 w-6" />
            <h1 className="text-3xl font-bold" data-testid="welcome-title">
              Benvenuto, {user?.first_name}
            </h1>
          </div>
          {user?.custom_role && (
            <p className="text-gray-600">Ruolo: <span className="font-semibold">{user.custom_role}</span></p>
          )}
        </div>

        {user?.requires_profile_completion && !isProfileComplete && (
          <div className="bg-red-50 border border-red-200 p-4 sm:p-6 mb-6 sm:mb-8 rounded-lg shadow-sm">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-6 w-6 text-red-600 mt-0.5 shrink-0" />
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-red-700 mb-1">Profilo incompleto</h3>
                  <p className="text-sm sm:text-base text-red-700">
                    Per poter accedere al sito è necessario compilare il tuo profilo personale. Mancano le seguenti informazioni:
                  </p>
                  <ul className="mt-2 list-disc list-inside text-sm sm:text-base text-red-700 space-y-1">
                    {missingProfileFields.map((fieldName) => (
                      <li key={fieldName}>{fieldName}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <Link to="/profile" className="w-full sm:w-auto">
                <Button className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white font-semibold">
                  Vai al mio profilo
                </Button>
              </Link>
            </div>
          </div>
        )}

        {/* Unpaid Quote Alert */}
        {currentPeriodUnpaidQuote.length > 0 && (
          <div className="bg-blue-900 text-white p-4 sm:p-6 mb-6 sm:mb-8 rounded-lg border border-blue-800 shadow-lg">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h3 className="text-lg sm:text-xl font-bold mb-1">⚠️ Quote associative da pagare!</h3>
                <p className="text-sm sm:text-base text-blue-100">
                  Hai {currentPeriodUnpaidQuote.length} quota{currentPeriodUnpaidQuote.length > 1 ? 'e' : ''} non {currentPeriodUnpaidQuote.length > 1 ? 'pagate' : 'pagata'}.
                </p>
              </div>
              <Button
                onClick={() => setShowQuoteModal(true)}
                className="w-full sm:w-auto bg-white text-blue-900 hover:bg-blue-50 font-semibold"
              >
                Visualizza Dettagli
              </Button>
            </div>
          </div>
        )}

        {/* Dashboard Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Prossime Attività */}
          <Card data-testid="activities-card">
            <CardHeader>
              <CardTitle>Prossime attività</CardTitle>
              <CardDescription>Eventi e riunioni in programma</CardDescription>
            </CardHeader>
            <CardContent>
              {cardPermissionErrors.activities ? (
                <p className="text-red-600 text-sm">Permessi mancanti per visualizzare i dati di questa card.</p>
              ) : activities.length === 0 ? (
                <p className="text-gray-600 text-sm">Nessuna attività programmata</p>
              ) : (
                <div className="space-y-3">
                  {activities.map((activity) => (
                    <div key={activity.id} className="border-l-2 border-black pl-3" data-testid="activity-item">
                      <h4 className="font-semibold">{activity.title}</h4>
                      <p className="text-sm text-gray-600">
                        {formatUtcDateTime(activity.start_datetime)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
              {!cardPermissionErrors.activities && (
                <Link to="/activities">
                  <Button variant="link" className="mt-4 p-0" data-testid="view-all-activities">Vedi tutte →</Button>
                </Link>
              )}
            </CardContent>
          </Card>

          {/* Votazioni in corso - VotingSection Component */}
          <VotingSection />

          {/* Offerte di Lavoro */}
          <Card data-testid="jobs-card">
            <CardHeader>
              <CardTitle>Nuove offerte di lavoro</CardTitle>
              <CardDescription>Opportunità per i membri</CardDescription>
            </CardHeader>
            <CardContent>
              {cardPermissionErrors.jobs ? (
                <p className="text-red-600 text-sm">Permessi mancanti per visualizzare i dati di questa card.</p>
              ) : jobs.length === 0 ? (
                <p className="text-gray-600 text-sm">Nessuna offerta disponibile</p>
              ) : (
                <div className="space-y-3">
                  {jobs.map((job) => (
                    <div key={job.id} className="border-l-2 border-black pl-3" data-testid="job-item">
                      <h4 className="font-semibold">{job.title}</h4>
                      <p className="text-sm text-gray-600">{job.company}</p>
                    </div>
                  ))}
                </div>
              )}
              {!cardPermissionErrors.jobs && (
                <Link to="/jobs">
                  <Button variant="link" className="mt-4 p-0" data-testid="view-all-jobs">Vedi tutte →</Button>
                </Link>
              )}
            </CardContent>
          </Card>

          {/* Novità membri */}
          <Card data-testid="news-card">
            <CardHeader>
              <CardTitle>Novità membri</CardTitle>
              <CardDescription>Aggiornamenti dalla community</CardDescription>
            </CardHeader>
            <CardContent>
              {cardPermissionErrors.news ? (
                <p className="text-red-600 text-sm">Permessi mancanti per visualizzare i dati di questa card.</p>
              ) : news.length === 0 ? (
                <p className="text-gray-600 text-sm">Nessuna novità</p>
              ) : (
                <div className="space-y-3">
                  {news.map((item) => (
                    <div key={item.id} className="border-l-2 border-black pl-3" data-testid="news-item">
                      <h4 className="font-semibold">{item.title}</h4>
                      <p className="text-sm text-gray-600">{item.author_name}</p>
                    </div>
                  ))}
                </div>
              )}
              {!cardPermissionErrors.news && (
                <Link to="/member-news">
                  <Button variant="link" className="mt-4 p-0" data-testid="view-all-news">Vedi tutte →</Button>
                </Link>
              )}
            </CardContent>
          </Card>

        </div>
      </div>

      {/* Unpaid Quote Modal */}
      <Dialog open={showQuoteModal} onOpenChange={setShowQuoteModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Quote associative non pagate</DialogTitle>
            <DialogDescription>
              Di seguito trovi le quote associative che risultano ancora da saldare
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {currentPeriodUnpaidQuote.length === 0 && (
              <p className="text-sm text-gray-600">Nessuna quota non pagata nel periodo corrente.</p>
            )}

            {currentPeriodUnpaidQuote.map((quota) => (
              <Card key={quota.id} className="border-l-4 border-l-red-500">
                <CardContent className="pt-6">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {quota.gruppo_name}
                      </h3>
                      <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium">
                        Non Pagata
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500 mb-1">Periodo</p>
                        <p className="font-medium">
                          {new Date(quota.data_inizio).toLocaleDateString('it-IT')} - {new Date(quota.data_fine).toLocaleDateString('it-IT')}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 mb-1">Importo da Saldare</p>
                        <p className="font-bold text-xl text-red-600">€ {quota.prezzo.toFixed(2)}</p>
                      </div>
                    </div>

                    <div className="pt-4 border-t flex justify-between items-center">
                      <p className="text-xs text-gray-500">
                        💡 Clicca su "Paga ora" per procedere con il pagamento sicuro tramite Stripe
                      </p>
                      <Button
                        onClick={() => handlePayQuota(quota.id)}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        💳 Paga ora
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-6 flex justify-end">
            <Button onClick={() => setShowQuoteModal(false)} variant="outline">
              Chiudi
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
